import { useState } from "react";
import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * ContactsFAQ — collapsible questions block.
 *
 * Accordion behaviour: clicking a row toggles its own open state, and the
 * body reveal is height-animated via a transitioned max-height. Only one
 * row can be open at a time — that's a common "quiet" pattern and avoids
 * the visual noise of every answer being open.
 */

interface QA {
  q: string;
  a: string;
}

const FAQS: QA[] = [
  {
    q: "How long is a session?",
    a: "Individual sessions run 50 minutes; couples sessions run 80 minutes. Online sessions match those durations exactly.",
  },
  {
    q: "How often should we meet?",
    a: "Weekly is the most common rhythm for the first few months. Some clients later move to fortnightly. We'll figure out the right cadence together — there is no fixed rule.",
  },
  {
    q: "What if I need to cancel or reschedule?",
    a: "You can move your appointment up to 24 hours in advance at no charge. Inside 24 hours, we still hold the session for you, and ask a small fee that covers the room.",
  },
  {
    q: "Is my information confidential?",
    a: "Yes — everything shared inside a session is confidential. The only exceptions are the legal safeguards required for the safety of a client or a minor. We'll walk through this on the first session.",
  },
  {
    q: "Do you accept insurance or reimburse via health plans?",
    a: "We can issue Portuguese ADSE-compatible receipts and receipts formatted for most private plans. Direct third-party billing is not something we offer at the moment.",
  },
  {
    q: "What if I've never done therapy before?",
    a: "Most first-timers arrive nervous — that's a normal way to arrive. You don't need to prepare anything. We'll start slowly and follow whatever thread feels alive.",
  },
];

export default function ContactsFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const listRef = useEnterAnimation<HTMLDivElement>({ delay: 300 });

  return (
    <section
      id="contacts-faq"
      className="relative w-full bg-background-50"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Contacts / 05
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 05 · Common questions
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex items-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left header */}
          <div className="lg:col-span-5">
            <div ref={eyebrowRef} className="mb-8 flex items-center gap-3">
              <span
                className="font-heading"
                style={{
                  fontSize: "clamp(2.75rem, 4vw, 4rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.03em",
                  color: "oklch(var(--foreground-950) / 0.35)",
                  lineHeight: 1,
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                05
              </span>
              <span className="w-12 h-[1px] bg-foreground-950/25"></span>
              <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
                Questions
              </span>
            </div>

            <h2
              ref={titleRef}
              className="mb-8"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(2.5rem, 4.8vw, 4.75rem)",
                fontWeight: 300,
                letterSpacing: "-0.025em",
                lineHeight: 1.05,
                color: "oklch(var(--foreground-950))",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              A few things
              <br />
              <span className="italic text-foreground-950/85">
                people often ask.
              </span>
            </h2>

            <p
              className="italic max-w-[380px]"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(1rem, 1.15vw, 1.15rem)",
                fontWeight: 300,
                lineHeight: 1.55,
                color: "oklch(var(--foreground-950) / 0.7)",
              }}
            >
              If your question isn&rsquo;t here, ask it in the form &mdash;
              we&rsquo;ll answer it personally.
            </p>
          </div>

          {/* Right accordion */}
          <div ref={listRef} className="lg:col-span-7 w-full">
            <div className="flex flex-col divide-y divide-foreground-950/12 border-y border-foreground-950/12">
              {FAQS.map((faq, i) => {
                const isOpen = openIndex === i;
                return (
                  <div key={faq.q} className="py-2">
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      className="group w-full flex items-start justify-between gap-5 py-4 md:py-5 text-left cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        <span
                          className="mt-1 font-heading text-foreground-950/45"
                          style={{
                            fontSize: "13px",
                            letterSpacing: "0.05em",
                            fontFeatureSettings: "'tnum' on",
                          }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span
                          className="text-foreground-950"
                          style={{
                            fontFamily: "var(--font-heading)",
                            fontSize: "clamp(1.15rem, 1.45vw, 1.5rem)",
                            fontWeight: 400,
                            lineHeight: 1.35,
                            letterSpacing: "-0.005em",
                          }}
                        >
                          {faq.q}
                        </span>
                      </div>
                      <span
                        className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full border transition-all duration-300 ${
                          isOpen
                            ? "bg-foreground-950 text-background-50 border-foreground-950 rotate-45"
                            : "bg-background-50 text-foreground-950 border-foreground-950/25 group-hover:border-foreground-950/60"
                        }`}
                      >
                        <i className="ri-add-line text-base"></i>
                      </span>
                    </button>

                    <div
                      className="overflow-hidden transition-all duration-500 ease-out"
                      style={{
                        maxHeight: isOpen ? "320px" : "0px",
                        opacity: isOpen ? 1 : 0,
                      }}
                    >
                      <p
                        className="pl-9 pr-4 pb-6 max-w-[640px]"
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: "clamp(0.95rem, 1.05vw, 1.1rem)",
                          lineHeight: 1.7,
                          color: "oklch(var(--foreground-950) / 0.8)",
                        }}
                      >
                        {faq.a}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}